import { inject, NgModule } from '@angular/core';
import { CanActivateFn, RouterModule, Routes } from '@angular/router';
import { LoginComponent } from './components/authentication/login/login.component';
import { PageNotFoundComponent } from './components/error-pages/page-not-found/page-not-found.component';
import { SetupTotpComponent } from './components/authentication/setup-totp/setup-totp.component';
import { ResetPasswordComponent } from './components/authentication/reset-password/reset-password.component';
import {
	AuthenticationCodeComponent
} from './components/authentication/authentication-code/authentication-code.component';
import { ForbiddenComponent } from './components/error-pages/forbidden/forbidden.component';
import { UnauthorizedComponent } from './components/error-pages/unauthorized/unauthorized.component';
import { BadGatewayComponent } from './components/error-pages/bad-gateway/bad-gateway.component';
import { AuthLayoutComponent } from './components/authentication/auth-layout.component';
import { ServerErrorComponent } from './components/error-pages/server-error/server-error.component';
import { SigninGuard } from './auth/signin.guard';
import { AuthGuard } from './auth/auth.guard';
import { PermissionCode } from 'src/app/api-models/kai-api-site-settings';
import { resolveSiteSettingData } from './site-setting.resolver';
import { SingleSignOnComponent } from './components/authentication/single-sign-on.component';
import { getMatchOnSiteSettingGuard } from './auth/site-setting.guard';
import { SiteSetting } from './components/shared/services/site-settings/site-settings/site-setting';
import { AppSettingService } from './app-setting.service';
import { constants } from 'src/constants/constants';
import { legacyRoutes } from './legacy-route-redirects';

export const helpRedirectGuard: CanActivateFn = async () => {
	const appSettingService = inject(AppSettingService);

	await resolveSiteSettingData();

	if (constants.jurisdictions.isWesternAustralia) return true;

	const url = appSettingService.appSetting.bulkIngestionApiUrl;
	window.location.href = `${url}swagger/index.html`;

	return false;
};

const routes: Routes = [
	// Redirect empty route to home page
	{
		path: '',
		redirectTo: 'search',
		pathMatch: 'full',
	},

	// Error pages
	{ path: 'forbidden', component: ForbiddenComponent },
	{ path: 'server-error', component: ServerErrorComponent },
	{ path: 'unauthorized', component: UnauthorizedComponent },
	{ path: 'bad-gateway', component: BadGatewayComponent },

	//SSO login
	{ path: 'sso', component: SingleSignOnComponent },

	//Bulk ingestion API swagger
	{
		path: 'help',
		canActivate: [helpRedirectGuard],
		component: PageNotFoundComponent,
	},

	// lazy loading the settings module
	{
		path: 'settings',
		loadChildren: () => import('./components/settings/settings.module').then((m) => m.IdsSettingsModule),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},

	// lazy loading the user-admin module
	{
		path: 'user-admin',
		loadChildren: () => import('./components/admin/user-admin.module').then((m) => m.IdsUserAdminModule),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},

	// lazy loading map module
	{
		path: 'map',
		resolve: [resolveSiteSettingData],
		loadChildren: () => import('./components/maps/maps.module').then((m) => m.IdsMapsModule),
		canLoad: [AuthGuard],
	},

	// lazy loading audit module
	{
		path: 'audit-logs',
		loadChildren: () => import('./components/audit-logs/audit-logs.module').then((m) => m.IdsAuditLogsModule),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},

	// lazy loading data-entry module
	{
		path: 'data-entry',
		loadChildren: () => import('./components/data-entry/data-entry.module').then((m) => m.IdsDataEntryModule),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
		data: {
			permissions: [PermissionCode.Property_Update],
		},
	},
	// lazy loading data-manager module
	{
		path: 'data-entry-grid',
		loadChildren: () =>
			import('./components/data-management/data-management.module').then((m) => m.IdsDataManagementModule),
		canLoad: [AuthGuard],
		data: {
			permissions: [PermissionCode.Property_View],
		},
		resolve: { siteSettings: resolveSiteSettingData },
	},

	// lazy loading modelling module
	{
		path: 'modelling',
		loadChildren: () => import('./components/modelling/modelling.module').then((m) => m.IdsModellingModule),
		canLoad: [AuthGuard],
		data: {
			permissions: [PermissionCode.Modelling2_Access],
		},
		resolve: { siteSettings: resolveSiteSettingData },
	},
	// lazy loading property module
	{
		path: 'properties',
		loadChildren: () => import('./components/property/property.module').then((m) => m.IdsPropertyModule),
		canLoad: [AuthGuard],
		data: {
			permissions: [PermissionCode.Property_View],
		},
		resolve: { siteSettings: resolveSiteSettingData },
	},
	// lazy loading permit module
	{
		path: 'permits',
		loadChildren: () => import('./components/permits/permits.module').then((m) => m.IdsPermitsModule),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},
	// lazy loading supplementary-view module
	{
		path: 'supplementary-view',
		loadChildren: () =>
			import('./components/supplementary-view/supplementary-view-grid.module').then(
				(m) => m.IdsSupplementaryViewGridModule
			),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},
	// lazy loading data link module
	{
		path: 'data-link',
		loadChildren: () => import('./components/data-link/data-link.module').then((m) => m.IdsDataLinkModule),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},
	// lazy loading data check module
	{
		path: 'data-check',
		loadChildren: () =>
			import('./components/data-check-count/data-check-count.module').then((m) => m.IdsDataCheckCountModule),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},

	// lazy loading analysis module
	{
		path: 'analysis',
		loadChildren: () => import('./components/analysis/analysis.module').then((m) => m.IdsAnalysisModule),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},
	// lazy loading programs module
	{
		path: 'programs',
		loadChildren: () =>
			import('./components/program-management/program-management.module').then(
				(m) => m.IdsProgramManagementModule
			),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},
	// lazy loading search module
	{
		path: 'search',
		loadChildren: () => import('./components/search/search.module').then((m) => m.IdsSearchModule),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},
	// lazy loading land module
	{
		path: 'land-management',
		loadChildren: () =>
			import('./components/land/land-workspace/land-workspace.module').then((m) => m.IdsLandWorkspaceModule),
		canLoad: [AuthGuard],
		resolve: [resolveSiteSettingData],
		canMatch: [getMatchOnSiteSettingGuard(SiteSetting.LandWorkspace)],
	},
	{
		path: 'lands',
		loadChildren: () => import('./components/land/land-page/land-page.module').then((m) => m.IdsLandPageModule),
		canLoad: [AuthGuard],
		resolve: [resolveSiteSettingData],
		canMatch: [getMatchOnSiteSettingGuard(SiteSetting.LandWorkspace)],
	},
	// lazy loading sale module
	{
		path: 'sales',
		loadChildren: () => import('./components/sales/sale-page/sale-page.module').then((m) => m.IdsSalePageModule),
		canLoad: [AuthGuard],
		resolve: [resolveSiteSettingData],
	},
	// lazy loading tenement management module
	{
		path: 'tenement',
		loadChildren: () =>
			import('./components/tenement/tenement-workspace/tenement-workspace.module').then(
				(m) => m.IdsTenementWorkspaceModule
			),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},
	// lazy loading report module
	{
		path: 'reports',
		loadChildren: () => import('./components/reports/reports.module').then((m) => m.IdsReportsPageModule),
		canLoad: [AuthGuard],
		resolve: [resolveSiteSettingData],
	},
	// lazy loading objections module
	{
		path: 'objections',
		loadChildren: () =>
			import('./components/objections/objections-grid.module').then((m) => m.IdsObjectionGridModule),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},
	// lazy loading manual overrides management module
	{
		path: 'manual-overrides-management',
		loadChildren: () =>
			import('./components/manual-overrides/manual-overrides-management/manual-overrides-management.module').then(
				(m) => m.IdsManualOverridesManagementModule
			),
		canLoad: [AuthGuard],
		resolve: [resolveSiteSettingData],
	},
	// Authorisation pages
	{
		path: '',
		component: AuthLayoutComponent,
		children: [
			{
				path: 'login',
				component: LoginComponent,
				data: { documentTitle: 'Login' },
				pathMatch: 'full',
				canActivate: [SigninGuard],
			},
			{
				path: 'reset-password',
				component: ResetPasswordComponent,
				data: { documentTitle: 'Reset Password' },
				pathMatch: 'full',
			},
			{
				path: 'authenticate',
				component: AuthenticationCodeComponent,
				data: { documentTitle: 'Authenticate' },
				pathMatch: 'full',
			},
			{
				path: 'setup-totp',
				component: SetupTotpComponent,
				data: { documentTitle: 'Setup Authenticator App' },
				pathMatch: 'full',
			},
		],
	},

	// Admin Home page
	{
		path: 'admin-home',
		loadChildren: () => import('./components/admin/home/home.module').then((m) => m.IdsHomeModule),
		resolve: [resolveSiteSettingData],
		canLoad: [AuthGuard],
	},

	// Redirect old VM Online (v10) query-string deep links onto the new routes.
	...legacyRoutes,

	{ path: '**', component: PageNotFoundComponent },
];

@NgModule({
	imports: [RouterModule.forRoot(routes, {})],
	exports: [RouterModule],
})
export class AppRoutingModule {}
